import type React from 'react';
import type { IndicationsBoxController } from '@/components/boxes/indicationsBoxes/indicationBoxController.ts';
import type { BoolFunction } from '@/components/boxes/hooks/types.ts';
import { type IBaseViewModel, SceneItemKind } from '@/engine/app/interfaces.ts';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { getConditionalStyles } from '@/components/boxes/styles.ts';
import { StyledTableCell, UpdatableResetIconButton } from '@/components/boxes/BoxTools.tsx';
import { EyeCheckboxButton } from '@/components/boxes/CheckboxButton';

import { useTheme } from '@mui/material/styles';
import {
  AreaIndicationIcon,
  DistanceIndicationIcon,
  SplineIndicationIcon,
  WorkflowIndicationIcon
} from '@/components/boxes/box-containers/boxContainers.ts';

import { observer } from 'mobx-react';
import { UpdatableValue } from '../../common/UpdatableValue';

import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';

interface Props {
  indication: IBaseViewModel;
  labelOverride?: string;
  mainController: IndicationsBoxController;
  isSelected: BoolFunction;
  isHighlighted: BoolFunction;
  index: number;
}

export const WorkflowIndicationRow: React.FC<Props> = observer(
  ({ indication, labelOverride, mainController, isSelected, isHighlighted, index }) => {
    const theme = useTheme();
    const isItemSelected: boolean = isSelected(indication.descriptor);
    const isItemHighlighted: boolean = isHighlighted(indication.descriptor);

    return (
      <TableRow
        key={index}
        data-testid={indication.descriptor + 'Distance'}
        sx={{
          ...{
            '&:last-child td, &:last-child th': { border: 0 },
            '&:hover button.locate-button': {
              opacity: 1
            },
            '& button.locate-button': { opacity: 0.1 }
          },
          ...getConditionalStyles(
            isItemHighlighted,
            theme,
            { paddingLeft: 0.3, paddingTop: 0, paddingBottom: 0 },
            {
              paddingRight: 0.5,
              paddingTop: 0,
              paddingBottom: 0
            }
          )
        }}
        onMouseEnter={(): void => {
          indication.setHighlighting(true);
        }}
        onMouseLeave={(): void => {
          indication.setHighlighting(false);
        }}
        selected={isItemSelected}
        onClick={(): void => {
          mainController.setIndicationSelection(indication.descriptor, !isItemSelected);
        }}
      >
        <StyledTableCell
          align="center"
          key={'criteria' + index}
          className="limited-width cell-height"
          component="td"
          scope="row"
        >
          {indication.kind === SceneItemKind.SplineCurve && <SplineIndicationIcon fontSize={'small'} />}
          {(indication.kind === SceneItemKind.SplineAreaMeasurement ||
            indication.kind === SceneItemKind.UnrestrictedSplineAreaMeasurement ||
            indication.kind === SceneItemKind.AreaMeasurement) && <AreaIndicationIcon fontSize={'small'} />}
          {indication.kind === SceneItemKind.CommissurePoint && <WorkflowIndicationIcon fontSize={'small'} />}
          {(indication.kind === SceneItemKind.LabeledDistanceMeasurement ||
            indication.kind === SceneItemKind.DistanceMeasurement) && <DistanceIndicationIcon fontSize={'small'} />}
        </StyledTableCell>
        <TableCell
          component="td"
          align={'left'}
          scope="row"
          key={'label' + index}
          sx={{
            color: theme.mode === 'dark' ? 'inherit' : theme.palette.text.primary,
            width: '18em'
          }}
        >
          {labelOverride ?? indication.label}
        </TableCell>
        <TableCell
          size="small"
          align="right"
          key={'value' + index}
          sx={{
            '&': {
              paddingRight: 0,
              width: '6rem',
              color: theme.mode === 'dark' ? 'inherit' : theme.palette.text.primary
            }
          }}
        >
          {indication.kind === SceneItemKind.SplineCurve && (
            <UpdatableResetIconButton descriptor={indication.descriptor} controller={mainController} />
          )}
          {(indication.kind === SceneItemKind.DistanceMeasurement ||
            indication.kind === SceneItemKind.LabeledDistanceMeasurement) && (
            <>
              <UpdatableValue
                descriptor={indication.descriptor}
                valueKey={'distance'}
                constructor={DT.DistanceMeasurement}
              />
              <span>{' mm'}</span>
            </>
          )}
          {(indication.kind === SceneItemKind.AreaMeasurement ||
            indication.kind === SceneItemKind.SplineAreaMeasurement ||
            indication.kind === SceneItemKind.UnrestrictedSplineAreaMeasurement) && (
            <>
              <UpdatableValue descriptor={indication.descriptor} valueKey={'area'} constructor={DT.AreaMeasurement} />
              <span>{' mm²'}</span>
            </>
          )}
        </TableCell>
        <StyledTableCell
          align="right"
          key={'visibility' + index}
          className="limited-width"
          onClick={(event): void => event.stopPropagation()}
        >
          <EyeCheckboxButton
            onChange={(isVisible: boolean): void => {
              mainController.setIndicationVisibility(indication.descriptor, isVisible);
            }}
            checked={indication.isVisible}
          />
        </StyledTableCell>
      </TableRow>
    );
  }
);
